import { inject, injectable } from 'tsyringe';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import type { AdHocAggregateRow, AnalyticsField, AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';
import { GlobalFilterController } from '@/features/global-filters/GlobalFilterController';
import { resolveWindowDays } from '@/features/global-filters/GlobalFilterTypes';
import { humanUser } from '@/shared/analytics/MetricFormatting';
import type { VizAccent } from '@/design/VizAccents';
import { ADOPTION_DIMENSIONS } from './AdoptionDimensions';
import { complexityScore, compositeScore, consistencyScore, frequencyScore, meanDifficultyLevel, trailingMean, volumeScore, type DriverScores } from './AdoptionScoring';
import type { AdoptionBreakdownRow, AdoptionContext, AdoptionDriver, AdoptionDriverKey, AdoptionScope, AdoptionScore, AdoptionTrend } from './AdoptionTypes';
import type { AdoptionGateway } from './AdoptionGateway';

interface DriverMeta {
    key: AdoptionDriverKey;
    label: string;
    description: string;
    weight: number;
    accent: VizAccent;
}

const DRIVER_META: DriverMeta[] = [
    { key: 'volume', label: 'Activity', description: 'Meaningful throughput per active user', weight: 20, accent: 'cyan' },
    { key: 'frequency', label: 'Frequency', description: 'Active days per week (of 5)', weight: 30, accent: 'indigo' },
    { key: 'complexity', label: 'Complexity', description: 'Low / Medium / High task mix', weight: 25, accent: 'amber' },
    { key: 'consistency', label: 'Consistency', description: 'Share of weeks active (habit)', weight: 25, accent: 'rose' },
];

/** Illustrative peer benchmarks (no cross-tenant data source in-app yet). */
const BENCHMARK_MEDIAN = 58;
const BENCHMARK_TOP_QUARTILE = 72;
/** Normalizes the window's allocated spend to the "Spend / mo" column (52 weeks / 12 months). */
const WEEKS_PER_MONTH = 52 / 12;

function mean(values: number[]): number {
    return values.length === 0 ? 0 : values.reduce((sum, v) => sum + v, 0) / values.length;
}

function delta4w(series: number[]): number {
    return Math.round(trailingMean(series, 4) - trailingMean(series.slice(0, -4), 4));
}

/** Scales a real per-week shape so its trailing-4-week mean equals the true headline value. */
function anchorToHeadline(raw: number[], target: number): number[] {
    const m = trailingMean(raw, 4);
    if (m <= 0) return raw.map(() => Math.round(target));
    const k = target / m;
    return raw.map((v) => Math.round(Math.max(0, Math.min(100, v * k))));
}

function formatWeek(bucket: string): string {
    const d = new Date(bucket);
    return Number.isNaN(d.getTime()) ? bucket : `${d.getMonth() + 1}/${d.getDate()}`;
}

function percentilePosition(score: number): number {
    if (score <= BENCHMARK_MEDIAN) return Math.round((score / BENCHMARK_MEDIAN) * 50);
    if (score <= BENCHMARK_TOP_QUARTILE) return Math.round(50 + ((score - BENCHMARK_MEDIAN) / (BENCHMARK_TOP_QUARTILE - BENCHMARK_MEDIAN)) * 25);
    return Math.round(75 + ((score - BENCHMARK_TOP_QUARTILE) / (100 - BENCHMARK_TOP_QUARTILE)) * 24);
}

/** Argmax dimension value per user, by turns — the user's dominant task/provider/model. */
function dominantByUser(rows: AdHocAggregateRow[], field: AnalyticsField): Map<string, string> {
    const best = new Map<string, { value: string; turns: number }>();
    for (const row of rows) {
        const userId = row.keys['user_id'];
        const value = row.keys[field];
        if (userId === undefined || value === undefined) continue;
        const current = best.get(userId);
        if (current === undefined || row.metrics.turns > current.turns) best.set(userId, { value, turns: row.metrics.turns });
    }
    return new Map([...best].map(([userId, { value }]) => [userId, value]));
}

/**
 * Live adoption gateway. Computes the composite + four drivers from the telemetry
 * OLAP layer via {@link IAnalyticsGateway}: Frequency/Consistency from per-user
 * active-days/weeks, Volume from interactions/active-user/week, and Complexity
 * from the difficulty mix. Headline values apply the algorithm exactly; all four
 * weekly driver series use a real per-week shape anchored (via `anchorToHeadline`)
 * so their trailing-4-week mean equals the headline — keeping each sparkline and
 * the composite `weekly` consistent with the displayed score, so the 4-week delta
 * can never imply an out-of-range prior value (e.g. score 24 with +31 pts). The
 * breakdown folds in directory identities with no activity as inactive rows (all
 * scores 0), so they surface in the grid and the overview's Inactive segment
 * rather than silently dropping out. A future bmai-insights endpoint will supply
 * per-week active-days directly behind this same contract.
 */
@injectable()
export class OlapAdoptionGateway implements AdoptionGateway {
    constructor(
        @inject(IAnalyticsGatewayToken) private readonly gateway: IAnalyticsGateway,
        @inject(GlobalFilterController) private readonly globalFilters: GlobalFilterController,
    ) {}

    private windowWeeks(): number {
        return Math.max(1, resolveWindowDays(this.globalFilters.snapshot().dateRange) / 7);
    }

    private async headlineDrivers(filters: AnalyticsFilter[]): Promise<{ scores: DriverScores; activeUsers: number }> {
        const weeks = this.windowWeeks();
        const [freqRows, difficulty] = await Promise.all([
            this.gateway.userActivityFrequency({ filters }),
            this.gateway.aggregate({ source: 'interactions', groupBy: ['difficulty'], metrics: ['turns'], filters, presentDimensions: ['difficulty'] }),
        ]);
        const activeUsers = freqRows.length;
        const frequency = Math.round(mean(freqRows.map((u) => frequencyScore(u.activeDays / weeks))));
        const consistency = Math.round(mean(freqRows.map((u) => consistencyScore(u.activeWeeks, weeks))));
        const totalTurns = freqRows.reduce((sum, u) => sum + u.turns, 0);
        const volume = volumeScore(activeUsers === 0 ? 0 : totalTurns / activeUsers / weeks);
        const complexity = complexityScore(meanDifficultyLevel(difficulty.rows.map((r) => ({ value: r.keys['difficulty'] ?? '', turns: r.metrics.turns }))));
        return { scores: { frequency, consistency, complexity, volume }, activeUsers };
    }

    private async weeklySeries(filters: AnalyticsFilter[], headline: DriverScores): Promise<{ labels: string[]; series: Record<AdoptionDriverKey, number[]> }> {
        const [activity, difficulty] = await Promise.all([
            this.gateway.trend({ source: 'interactions', bucket: 'week', metrics: ['turns', 'users', 'sessions'], filters }),
            this.gateway.trend({ source: 'interactions', bucket: 'week', groupBy: 'difficulty', metrics: ['turns'], filters }),
        ]);
        const buckets = activity.points.map((p) => p.bucket);
        const labels = buckets.map(formatWeek);

        const volumeShape = activity.points.map((p) => volumeScore(p.metrics.users > 0 ? p.metrics.turns / p.metrics.users : 0));

        const levelsByBucket = new Map<string, { value: string; turns: number }[]>();
        for (const point of difficulty.points) {
            const list = levelsByBucket.get(point.bucket) ?? [];
            list.push({ value: point.key ?? '', turns: point.metrics.turns });
            levelsByBucket.set(point.bucket, list);
        }
        const complexityShape = buckets.map((b) => complexityScore(meanDifficultyLevel(levelsByBucket.get(b) ?? [])));

        const frequencyShape = activity.points.map((p) => frequencyScore(p.metrics.users > 0 ? p.metrics.sessions / p.metrics.users : 0));
        const consistencyShape = activity.points.map((p) => p.metrics.users);

        const volume = anchorToHeadline(volumeShape, headline.volume);
        const complexity = anchorToHeadline(complexityShape, headline.complexity);
        const frequency = anchorToHeadline(frequencyShape, headline.frequency);
        const consistency = anchorToHeadline(consistencyShape, headline.consistency);

        return { labels, series: { volume, frequency, complexity, consistency } };
    }

    async getScore(filters: AnalyticsFilter[]): Promise<AdoptionScore> {
        const [{ scores, activeUsers }, providers, directoryUsers] = await Promise.all([
            this.headlineDrivers(filters),
            this.gateway.aggregate({ source: 'interactions', groupBy: ['provider_connection_id'], metrics: ['turns'], filters }),
            this.gateway.directoryUserIds(),
        ]);
        const { labels, series } = await this.weeklySeries(filters, scores);

        const drivers: AdoptionDriver[] = DRIVER_META.map((m) => ({
            key: m.key,
            label: m.label,
            description: m.description,
            weight: m.weight,
            accent: m.accent,
            score: Math.round(scores[m.key]),
            deltaPts: delta4w(series[m.key]),
            weekly: series[m.key],
        }));

        const weekly = labels.map((_, i) =>
            compositeScore({ frequency: series.frequency[i] ?? 0, consistency: series.consistency[i] ?? 0, complexity: series.complexity[i] ?? 0, volume: series.volume[i] ?? 0 }),
        );
        const score = compositeScore(scores);
        const totalUsers = directoryUsers.length || activeUsers;
        const context: AdoptionContext = {
            reach: totalUsers === 0 ? 0 : activeUsers / totalUsers,
            activeUsers,
            totalUsers,
            providers: providers.distinctCount,
            coverage: 4,
            coverageTotal: 4,
        };

        return {
            score,
            deltaPts: delta4w(weekly),
            percentile: percentilePosition(score),
            benchmarkMedian: BENCHMARK_MEDIAN,
            benchmarkTopQuartile: BENCHMARK_TOP_QUARTILE,
            labels,
            weekly,
            drivers,
            context,
        };
    }

    async getBreakdown(filters: AnalyticsFilter[]): Promise<AdoptionBreakdownRow[]> {
        const weeks = this.windowWeeks();
        const [base, freqRows, byDifficulty, byTask, byProvider, byModel, directoryUsers] = await Promise.all([
            this.gateway.aggregate({ source: 'interactions', groupBy: ['user_id', 'user_department', 'user_title', 'user_org_unit', 'user_manager'], metrics: ['turns', 'totalTokens', 'allocatedCostUsd'], filters }),
            this.gateway.userActivityFrequency({ filters }),
            this.gateway.aggregate({ source: 'interactions', groupBy: ['user_id', 'difficulty'], metrics: ['turns'], filters, presentDimensions: ['difficulty'] }),
            this.gateway.aggregate({ source: 'interactions', groupBy: ['user_id', 'task_category'], metrics: ['turns'], filters }),
            this.gateway.aggregate({ source: 'interactions', groupBy: ['user_id', 'provider_connection_id'], metrics: ['turns'], filters }),
            this.gateway.aggregate({ source: 'interactions', groupBy: ['user_id', 'model'], metrics: ['turns'], filters }),
            this.gateway.directoryUsers(),
        ]);

        const freqByUser = new Map(freqRows.map((u) => [u.userId, u]));
        const levelsByUser = new Map<string, { value: string; turns: number }[]>();
        for (const row of byDifficulty.rows) {
            const userId = row.keys['user_id'];
            if (userId === undefined) continue;
            const list = levelsByUser.get(userId) ?? [];
            list.push({ value: row.keys['difficulty'] ?? '', turns: row.metrics.turns });
            levelsByUser.set(userId, list);
        }
        const taskByUser = dominantByUser(byTask.rows, 'task_category');
        const providerByUser = dominantByUser(byProvider.rows, 'provider_connection_id');
        const modelByUser = dominantByUser(byModel.rows, 'model');

        const activeRows = base.rows
            .map((row): AdoptionBreakdownRow | null => {
                const userId = row.keys['user_id'];
                if (userId === undefined) return null;
                const activity = freqByUser.get(userId);
                const frequency = frequencyScore((activity?.activeDays ?? 0) / weeks);
                const consistency = consistencyScore(activity?.activeWeeks ?? 0, weeks);
                const turns = activity?.turns ?? row.metrics.turns;
                const volume = volumeScore(turns / weeks);
                const complexity = complexityScore(meanDifficultyLevel(levelsByUser.get(userId) ?? []));
                const adoption = compositeScore({ frequency, consistency, complexity, volume });
                return {
                    id: userId,
                    label: humanUser(userId),
                    dims: {
                        department: row.keys['user_department'] ?? '—',
                        role: row.keys['user_title'] ?? '—',
                        orgUnit: row.keys['user_org_unit'] ?? '—',
                        manager: row.keys['user_manager'] ?? '—',
                        task: taskByUser.get(userId) ?? '—',
                        provider: providerByUser.get(userId) ?? '—',
                        model: modelByUser.get(userId) ?? '—',
                    },
                    adoption,
                    volume,
                    frequency,
                    complexity,
                    consistency,
                    reach: Math.min(1, (activity?.activeWeeks ?? 0) / weeks),
                    spend: Math.round(((row.metrics.allocatedCostUsd ?? 0) / weeks) * WEEKS_PER_MONTH * 100) / 100,
                    inactive: false,
                };
            })
            .filter((row): row is AdoptionBreakdownRow => row !== null);

        // Case-fold: activeRows.id (interactions user_id / email) and directory
        // userId (email) can differ in case, which would wrongly fold an active
        // user into the inactive list.
        const activeIds = new Set(activeRows.map((row) => row.id.toLowerCase()));
        const inactiveRows = directoryUsers
            .filter((user) => !activeIds.has(user.userId.toLowerCase()))
            .map((user): AdoptionBreakdownRow => ({
                id: user.userId,
                label: humanUser(user.userId),
                dims: {
                    department: user.department ?? '—',
                    role: user.title ?? '—',
                    orgUnit: user.orgUnit ?? '—',
                    manager: user.manager ?? '—',
                    task: '—',
                    provider: '—',
                    model: '—',
                },
                adoption: 0,
                volume: 0,
                frequency: 0,
                complexity: 0,
                consistency: 0,
                reach: 0,
                spend: 0,
                inactive: true,
            }));

        return [...activeRows, ...inactiveRows];
    }

    async getTrend(filters: AnalyticsFilter[], scopePath: AdoptionScope[]): Promise<AdoptionTrend> {
        const scoped = [...filters, ...scopePath.flatMap((scope) => this.scopeFilters(scope))];
        const { scores } = await this.headlineDrivers(scoped);
        const { labels, series } = await this.weeklySeries(scoped, scores);
        return { labels, drivers: DRIVER_META.map((m) => ({ key: m.key, score: Math.round(scores[m.key]), weekly: series[m.key] })) };
    }

    private scopeFilters(scope: AdoptionScope): AnalyticsFilter[] {
        const field = scope.dimension === 'user' ? 'user_id' : ADOPTION_DIMENSIONS.find((d) => d.columnKey === scope.dimension)?.field;
        return field === undefined ? [] : [{ field, values: [scope.value] }];
    }
}
